

import { useNavigate } from "react-router-dom";
import { useUtils } from "@/app/hooks/useUtils";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useMembershipVoucherListingAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVoucherListingAPI";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { PRIMARY_BUTTON } from "@/constants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { TbCircleLetterV } from "react-icons/tb";

const tableHeader = [
  { item: "Name" },
  { item: "Type" },
  { item: "Value" },
  { item: "Category" },
  { item: "Sub Category" },
  { item: "Service Type" },
  { item: "Status" },
  { item: "Created At" },
];

const VoucherListComponent = () => {
  const { veticDateFormat } = useUtils();
  const { NO_PWL_RESULTS } = useImageConstants();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const { execute: fetchVoucherList, voucherList, isVoucherListInProgress } = useMembershipVoucherListingAPI();

  useEffect(() => {
    fetchVoucherList(undefined, searchQuery);
  }, [searchQuery]);

  const handleCreateVoucher = () => {
    navigate("/app/voucher-setup/voucher-creation");
  };

  return (
    <div className="px-6 py-6 w-full min-h-screen bg-white">
      {/* Header */}
      <div className="mb-3">
        <h1 className="text-l font-semibold text-black">Voucher Families</h1>
      </div>

      {/* Search + Create */}
      <div className="flex justify-between items-center mb-4">
        <div className="w-1/4">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => setSearchQuery(query)}
            containerClassName="w-full"
            addon={<TbCircleLetterV className="w-4 h-4" />}
            placeholder="Search by Voucher Name"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
          />
        </div>
        <button onClick={handleCreateVoucher} className={PRIMARY_BUTTON}>
          Create Voucher
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isVoucherListInProgress ? (
          <TableSkeleton />
        ) : voucherList?.length === 0 ? (
          <div className="mt-6">
            {/* <img src={NO_PWL_RESULTS} alt="No vouchers" className="mx-auto" /> */}
            <p className="text-center mt-2 text-gray-500">
              {searchQuery?.trim()
                ? `No voucher families found for "${searchQuery.trim()}"`
                : "No voucher families available"}
            </p>
          </div>
        ) : (
          <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
            <div className="scroll-inner !h-auto max-h-[600px]">
              <div className="w-full">
                <div className="inline-block min-w-full align-middle">
                  <div className="bg-white rounded-lg">
                    <table className="w-full min-w-[900px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                      <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                        <tr>
                          {tableHeader?.map((key, index) => (
                            <th
                              key={index}
                              scope="col"
                              className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                            >
                              {key.item}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="overflow-hidden rounded-xl w-full">
                        {voucherList?.map((voucher, idx) => {
                          const isFirst = idx === 0;
                          const isLast = idx === voucherList?.length - 1;
                          const isSingle = voucherList?.length === 1;

                          return (
                            <tr
                              key={idx}
                              className={clsx(
                                "group bg-white transition",
                                isSingle && "rounded-xl",
                                isFirst && !isSingle && "rounded-t-xl",
                                isLast && !isSingle && "rounded-b-xl",
                              )}
                            >
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                  isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                  isFirst && !isSingle && "rounded-tl-2xl",
                                  isLast && !isSingle && "rounded-bl-2xl",
                                )}
                              >
                                {voucher.name}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {voucher.type}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">{voucher.value}</td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {voucher.category_name}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {voucher.sub_category_name}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                {voucher.service_type}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                <div
                                  className={clsx(
                                    "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium",
                                    voucher.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
                                  )}
                                >
                                  <span
                                    className={clsx(
                                      "w-2 h-2 rounded-full",
                                      voucher.is_active ? "bg-green-500" : "bg-red-500",
                                    )}
                                  />
                                  {voucher.is_active ? "Active" : "Inactive"}
                                </div>
                              </td>
                              <td
                                className={clsx(
                                  "px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]",
                                  isSingle && "rounded-tr-2xl rounded-br-2xl",
                                  isFirst && !isSingle && "rounded-tr-2xl",
                                  isLast && !isSingle && "rounded-br-2xl",
                                )}
                              >
                                {voucher.created_at ? veticDateFormat(voucher.created_at) : "NA"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoucherListComponent;
